/**
 * CompensationBuilder — internal Offer Compensation decision tool for
 * `/installer-offers/:jobId/review`.
 *
 * Three operator-controlled modes (no automation, no AI, no auctions):
 *   • Manual   — type a dollar amount directly.
 *   • Percent  — % of customer contract value
 *                (gated by useFinancialPermissions().viewContractValue).
 *   • Suggested — pick from accepted comparable offers (low / median / high)
 *                 only when sample ≥ MIN_SAMPLE_FOR_DISPLAY (3).
 *
 * ALL writes funnel through the existing canonical writer
 * `setJobPayoutAmount` via useSetJobPayoutAmount(). No second writer.
 * Never mutates `MarketplaceOffer.payoutAmountSnapshot` — snapshots are
 * captured only when an offer is sent.
 *
 * Contract value, percent mode, and any economics are NEVER exposed to
 * installer-facing surfaces. This component renders for internal roles only.
 */
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DollarSign, Lock, Check, Info } from "lucide-react";
import { toast } from "sonner";
import { PAYOUT_MATERIAL_DIVERGENCE_PCT, canSetJobPayoutAmount } from "@/data/contractor/setJobPayoutAmount";
import { useSetJobPayoutAmount } from "@/hooks/useSetJobPayoutAmount";
import { useRole } from "@/data/RoleContext";
import { useFinancialPermissions } from "@/hooks/useFinancialPermissions";
import type { Job } from "@/data/types";
import type { ComparableJobOffersSummary } from "@/data/contractor/comparableJobOffers";
import {
  getOfferCompensationDefaults,
  DEFAULT_OFFER_PCT_OF_NET,
} from "@/data/contractor/offerCompensationDefaults";

type PayoutChoice = "manual" | "percent" | "suggested" | "revenue_share";
type RevenueShareMode = "fixed_amount" | "percentage";
type IncentiveType = "five_star" | "other";
type IncentiveConfig = NonNullable<Job["offerCompensationConfig"]>["incentives"] extends Array<infer T> ? T : never;

interface IncentiveDraft {
  id: string;
  type: IncentiveType;
  amountStr: string;
  label: string;
}

interface Props {
  job: Job;
  comparables: ComparableJobOffersSummary;
  /**
   * Notifies parent of the current draft amount and whether it differs
   * from the saved `job.payoutAmount` baseline. Pure preview; no writes.
   * Called whenever the operator edits manual/percent/suggested inputs.
   */
  onDraftChange?: (draft: CompensationDraftPreview) => void;
  acceptedOffer?: { contractorName: string; acceptedAt: string } | null;
}

export interface CompensationDraftPreview {
  amount: number | null;
  isDraft: boolean;
  offerCompensationConfig?: Job["offerCompensationConfig"];
  incentiveTotal: number;
  effectiveOfferTotal: number;
  payoutOptionLabel: string;
  contractValue: number;
  financeFeePct: number;
  financeFeeAmount: number;
  netContract: number;
  canViewContract: boolean;
}

function ModeTab({
  label,
  active,
  disabled,
  onClick,
  hint,
}: {
  label: string;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  hint?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={hint}
      className={`flex-1 px-2.5 py-1.5 text-[11px] font-medium rounded-md transition-colors ${
        active
          ? "bg-primary/15 text-primary ring-1 ring-primary/30"
          : "text-muted-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
      }`}
    >
      {label}
    </button>
  );
}

export function CompensationBuilder({ job, comparables, onDraftChange, acceptedOffer }: Props) {
  const { role } = useRole();
  const fin = useFinancialPermissions();
  const { update } = useSetJobPayoutAmount();
  const canSet = canSetJobPayoutAmount(role);

  const [isEditingAccepted, setIsEditingAccepted] = useState(false);
  const [showEditConfirm, setShowEditConfirm] = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [editLockedChoice, setEditLockedChoice] = useState<PayoutChoice | null>(null);
  const [editLockedRevenueShareMode, setEditLockedRevenueShareMode] = useState<RevenueShareMode | null>(null);

  const baseline = job.payoutAmount ?? 0;
  const defaults = useMemo(() => getOfferCompensationDefaults(job), [job]);
  const contractValue = defaults.contractValue;
  const netContract = defaults.netContract;
  const canViewContract = fin.viewContractValue && contractValue > 0;
  const canViewComparables = fin.viewContractValue; // economics-bearing
  const suggestedAvailable = comparables.hasDisplayableSample;
  const acceptedLocked = !!acceptedOffer && !isEditingAccepted;
  const isChoiceLocked = !!acceptedOffer && isEditingAccepted && !!editLockedChoice;
  const isRevenueShareModeLocked = !!acceptedOffer && isEditingAccepted && !!editLockedRevenueShareMode;
  const savedConfig = job.offerCompensationConfig;
  const initialIncentives = useMemo<IncentiveDraft[]>(
    () =>
      (savedConfig?.incentives ?? []).map((item, index) => ({
        id: `saved-${index}-${item.type}`,
        type: item.type,
        amountStr: String(item.amount ?? 0),
        label: item.label ?? "",
      })),
    [savedConfig?.incentives],
  );
  const initialRevenueShareMode: RevenueShareMode =
    savedConfig?.payoutOption === "revenue_share" && savedConfig.revenueShareMode === "percentage"
      ? "percentage"
      : "fixed_amount";
  const derivedBaselinePct =
    savedConfig?.payoutOption === "revenue_share" &&
    savedConfig.revenueShareMode === "percentage" &&
    typeof savedConfig.percentageOfNet === "number"
      ? savedConfig.percentageOfNet
      : canViewContract && netContract > 0 && baseline > 0
        ? Number(((baseline / netContract) * 100).toFixed(2))
        : DEFAULT_OFFER_PCT_OF_NET;

  const initialChoice: PayoutChoice =
    savedConfig?.payoutOption === "revenue_share"
      ? "revenue_share"
      : savedConfig?.sourceMode === "suggested" && suggestedAvailable
        ? "suggested"
        : savedConfig?.sourceMode === "percent" && canViewContract
          ? "percent"
          : "manual";
  const [choice, setChoiceRaw] = useState<PayoutChoice>(initialChoice);
  const [revenueShareMode, setRevenueShareModeRaw] = useState<RevenueShareMode>(initialRevenueShareMode);
  const [incentives, setIncentives] = useState<IncentiveDraft[]>(initialIncentives);

  const isLockedChoice = (c: PayoutChoice) => isChoiceLocked && editLockedChoice === c;
  const isLockedRevenueShareMode = (m: RevenueShareMode) => isRevenueShareModeLocked && editLockedRevenueShareMode === m;

  const setRevenueShareMode = (next: RevenueShareMode) => {
    if (acceptedLocked) return;
    if (isRevenueShareModeLocked && editLockedRevenueShareMode && next !== editLockedRevenueShareMode) return;
    setRevenueShareModeRaw(next);
  };

  /**
   * Switching modes should preserve the dollar value the operator is
   * currently looking at — not snap back to the saved baseline. When
   * leaving Percent/Suggested for Manual, seed the Manual input with
   * the live `proposed` value so the number is continuous across tabs.
   */
  const setChoice = (next: PayoutChoice) => {
    if (acceptedLocked) return;
    if (isChoiceLocked && editLockedChoice && next !== editLockedChoice) return;
    if (next === "manual" && choice !== "manual" && proposed !== null) {
      setAmountStr(String(proposed));
    }
    if ((next === "percent" || next === "revenue_share") && choice !== next && canViewContract && netContract > 0) {
      // Sync pctStr from whatever amount is currently proposed (or the saved
      // baseline) so Calculated Offer reflects the manual/suggested amount.
      const amount = proposed !== null ? proposed : baseline;
      if (amount > 0) setPctStr(((amount / netContract) * 100).toFixed(2));
    }
    setChoiceRaw(next);
  };

  // Manual state
  const [amountStr, setAmountStr] = useState(String(baseline));
  // Percent state — % of NET contract (contract − assumed finance fee).
  // Always defaults to 15% per ops policy, regardless of any prior
  // saved baseline. The current saved amount is still visible in the
  // summary strip above ("Current Offer").
  const [pctStr, setPctStr] = useState(String(derivedBaselinePct));
  // Reason
  const [reason, setReason] = useState("");

  // Reset draft if baseline changes externally. Percent resets to the
  // saved payout-derived percent when possible; otherwise falls back to
  // default policy (15%).
  useEffect(() => {
    setAmountStr(String(baseline));
    setChoiceRaw(initialChoice);
    setRevenueShareModeRaw(initialRevenueShareMode);
    setIncentives(initialIncentives);
    if (canViewContract && netContract > 0 && baseline > 0) {
      // Derive pct from the saved baseline amount so round-tripping through
      // Percent mode shows the same dollar value (toFixed(2) precision avoids
      // the rounding gap that caused Calculated Offer to differ from Manual amount).
      setPctStr(((baseline / netContract) * 100).toFixed(2));
    } else if (canViewContract) {
      setPctStr(String(DEFAULT_OFFER_PCT_OF_NET));
    }
    setReason("");
  }, [baseline, canViewContract, netContract, initialChoice, initialIncentives, initialRevenueShareMode]);

  const proposed = useMemo(() => {
    if (choice === "manual" || (choice === "revenue_share" && revenueShareMode === "fixed_amount") || choice === "suggested") {
      const n = Number(amountStr);
      return Number.isFinite(n) ? Math.max(0, Math.round(n)) : null;
    }
    if (choice === "percent" || (choice === "revenue_share" && revenueShareMode === "percentage")) {
      const p = Number(pctStr);
      if (!Number.isFinite(p) || p < 0 || !canViewContract) return null;
      return Math.round((p / 100) * netContract);
    }
    return null;
  }, [choice, revenueShareMode, amountStr, pctStr, netContract, canViewContract]);

  const delta = proposed !== null ? proposed - baseline : 0;
  const hasChange = proposed !== null && proposed !== baseline;
  const pctDelta = baseline > 0 && proposed !== null ? Math.abs(delta) / baseline : 0;
  const material =
    baseline > 0 && proposed !== null && pctDelta >= PAYOUT_MATERIAL_DIVERGENCE_PCT;
  const canSave =
    canSet &&
    proposed !== null &&
    proposed <= 10_000_000;

  const normalizedIncentives = useMemo<IncentiveConfig[] | undefined>(() => {
    if (incentives.length === 0) return undefined;
    return incentives.map((item) => ({
      type: item.type,
      amount: Number(item.amountStr),
      ...(item.type === "other" && item.label.trim() ? { label: item.label.trim() } : {}),
      updatedAt: new Date().toISOString(),
    }));
  }, [incentives]);

  const incentivesValid = useMemo(
    () =>
      incentives.every((item) => {
        const amount = Number(item.amountStr);
        if (!Number.isFinite(amount) || amount <= 0) return false;
        if (item.type === "other" && item.label.trim().length === 0) return false;
        return true;
      }),
    [incentives],
  );

  const incentiveTotal = useMemo(
    () =>
      incentives.reduce((sum, item) => {
        const amount = Number(item.amountStr);
        return Number.isFinite(amount) && amount > 0 ? sum + Math.round(amount) : sum;
      }, 0),
    [incentives],
  );

  const nextConfig = useMemo<Job["offerCompensationConfig"]>(() => {
    if (choice === "revenue_share") {
      if (revenueShareMode === "percentage" && canViewContract) {
        const pct = Number(pctStr);
        return {
          payoutOption: "revenue_share",
          revenueShareMode: "percentage",
          percentageOfNet: Number.isFinite(pct) ? pct : undefined,
          fixedAmount: proposed ?? undefined,
          incentives: normalizedIncentives,
          updatedAt: new Date().toISOString(),
        };
      }
      return {
        payoutOption: "revenue_share",
        revenueShareMode: "fixed_amount",
        fixedAmount: proposed ?? undefined,
        incentives: normalizedIncentives,
        updatedAt: new Date().toISOString(),
      };
    }
    return {
      payoutOption: "fixed_payout",
      fixedAmount: proposed ?? undefined,
      sourceMode: choice === "suggested" ? "suggested" : choice === "percent" ? "percent" : "manual",
      incentives: normalizedIncentives,
      updatedAt: new Date().toISOString(),
    };
  }, [canViewContract, choice, revenueShareMode, pctStr, proposed, normalizedIncentives]);
  const configChanged =
    JSON.stringify(savedConfig ?? null) !== JSON.stringify(nextConfig ?? null);
  const canPersist = canSave && incentivesValid && (hasChange || configChanged);

  const handleSave = async () => {
    if (proposed === null) return;
    if (acceptedOffer && !isEditingAccepted) {
      setShowSaveConfirm(true);
      return;
    }
    const r = await update({
      jobId: job.id,
      amount: proposed,
      reason: reason.trim() || undefined,
      materialDivergence: material,
      offerCompensationConfig: nextConfig,
    });
    if (r.ok) {
      setReason("");
      setIsEditingAccepted(false);
      setEditLockedChoice(null);
      setEditLockedRevenueShareMode(null);
    }
  };

  const applySuggested = (v: number) => {
    setAmountStr(String(v));
  };

  const addIncentive = () => {
    if (acceptedOffer && !isEditingAccepted) {
      toast.info("Adding incentive to accepted offer", {
        description: "This incentive will be included in the active compensation.",
      });
    }
    setIncentives((prev) => [
      ...prev,
      {
        id: `draft-${Date.now()}-${prev.length}`,
        type: "five_star",
        amountStr: "",
        label: "",
      },
    ]);
  };

  // SINGLE SOURCE OF TRUTH for everything the operator sees in the
  // summary strip: the live `proposed` value (falling back to the saved
  // baseline only when no draft exists). Never mix baseline + proposed
  // in the same breakdown — that's what caused the % drift bug.
  const effectiveOffer = proposed !== null ? proposed : baseline;
  const effectiveOfferTotal = effectiveOffer + incentiveTotal;
  const isDraft = proposed !== null && proposed !== baseline;
  const payoutOptionLabel =
    choice === "revenue_share"
      ? revenueShareMode === "percentage"
        ? "Revenue Share · % of Net"
        : "Revenue Share · Fixed Amount"
      : choice === "suggested"
        ? "Suggested"
        : choice === "percent"
          ? "Percent"
          : "Manual";

  const offerPctOfNet =
    canViewContract && effectiveOfferTotal > 0 && netContract > 0
      ? (effectiveOfferTotal / netContract) * 100
      : null;

  useEffect(() => {
    if (!onDraftChange) return;
    onDraftChange({
      amount: proposed,
      isDraft,
      offerCompensationConfig: nextConfig,
      incentiveTotal,
      effectiveOfferTotal,
      payoutOptionLabel,
      contractValue,
      financeFeePct: defaults.financeFeePct,
      financeFeeAmount: defaults.financeFeeAmount,
      netContract,
      canViewContract,
    });
  }, [
    proposed,
    isDraft,
    nextConfig,
    incentiveTotal,
    effectiveOfferTotal,
    payoutOptionLabel,
    contractValue,
    defaults.financeFeePct,
    defaults.financeFeeAmount,
    netContract,
    canViewContract,
    onDraftChange,
  ]);

  return (
    <div className="space-y-3" data-testid="compensation-builder">
      {/* Header + badges */}
      <div>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold">Offer Compensation Builder</h3>
            {acceptedOffer && !isEditingAccepted && (
              <Badge variant="outline" className="text-[10px] border-green-500/40 text-green-600">Accepted</Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {acceptedOffer && !isEditingAccepted && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-[11px]"
                onClick={() => setShowEditConfirm(true)}
              >
                Edit
              </Button>
            )}
            {!canSet && (
              <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                <Lock className="h-3 w-3" /> Read-only
              </span>
            )}
          </div>
        </div>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          Set what this job pays the installer. Customer contract value stays internal.
        </p>
        {acceptedOffer && !isEditingAccepted && (
          <p className="mt-1.5 text-[10px] text-muted-foreground flex items-center gap-1">
            <Info className="h-3 w-3 mt-0.5 shrink-0"/>
            Offer accepted by {acceptedOffer.contractorName} on {new Date(acceptedOffer.acceptedAt).toLocaleDateString()}. Editing will modify the acceppted compensation.
          </p>
        )}
        <div className="mt-2 flex flex-wrap gap-1">
          <Badge variant="outline" className="text-[10px]">Internal Only</Badge>
          <Badge variant="outline" className="text-[10px]">Installer-Safe Amount</Badge>
          {choice === "suggested" && suggestedAvailable && (
            <Badge variant="outline" className="text-[10px]">Comparable-Based</Badge>
          )}
        </div>
      </div>

      {/* Summary strip — reflects the LIVE draft, not the saved baseline.
          When a draft is active we show both values so the operator sees
          what's being saved and what was previously saved. */}
      <div className="rounded-md border bg-muted/40 p-2.5 text-xs space-y-1">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">
            {isDraft ? "Base Payout (draft)" : acceptedOffer && !isEditingAccepted ? "Accepted Base Payout" : "Current Base Payout"}
          </span>
          <span className="tabular-nums font-medium">
            ${effectiveOffer.toLocaleString()}
            {isDraft && baseline > 0 && (
              <span className="ml-1.5 text-[10px] text-muted-foreground font-normal">
                was ${baseline.toLocaleString()}
              </span>
            )}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Incentives</span>
          <span className="tabular-nums">
            {incentiveTotal > 0 ? `+$${incentiveTotal.toLocaleString()}` : "—"}
          </span>
        </div>
        <div className="flex items-center justify-between border-t pt-1 mt-1">
          <span className="text-muted-foreground font-medium">
            {isDraft ? "Offer Total (draft)" : acceptedOffer && !isEditingAccepted ? "Accepted Offer Total" : "Current Offer Total"}
          </span>
          <span className="tabular-nums font-medium">
            ${effectiveOfferTotal.toLocaleString()}
          </span>
        </div>
        {canViewContract && (
          <>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Contract Value</span>
              <span className="tabular-nums">${contractValue.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">
                Assumed Finance Fee ({defaults.financeFeePct}%
                {defaults.dealKind === "cash" ? " · cash deal" : ""})
              </span>
              <span className="tabular-nums">
                −${defaults.financeFeeAmount.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between border-t pt-1 mt-1">
              <span className="text-muted-foreground font-medium">Net Contract</span>
              <span className="tabular-nums font-medium">
                ${netContract.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Payout Option</span>
              <span className="tabular-nums">{payoutOptionLabel}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Offer % of Net</span>
              <span className="tabular-nums">
                {offerPctOfNet !== null ? `${offerPctOfNet.toFixed(1)}%` : "—"}
              </span>
            </div>
          </>
        )}
        {suggestedAvailable && comparables.median !== null && (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">
              Suggested Median (n={comparables.count})
            </span>
            <span className="tabular-nums">
              ${comparables.median.toLocaleString()}
            </span>
          </div>
        )}
      </div>

      <div className="space-y-1">
        <label className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
          Payout Option
        </label>
        <div className="flex gap-1 p-1 rounded-md bg-muted/60">
          <ModeTab
            label="Manual"
            active={choice === "manual"}
            disabled={acceptedLocked || (isChoiceLocked && !isLockedChoice("manual"))}
            onClick={() => {
              if (acceptedLocked || (isChoiceLocked && !isLockedChoice("manual"))) return;
              setChoice("manual");
            }}
          />
          <ModeTab
            label="Percent"
            active={choice === "percent"}
            disabled={acceptedLocked || (isChoiceLocked ? !isLockedChoice("percent") : !canViewContract)}
            hint={!canViewContract && !isLockedChoice("percent") ? "Contract value not visible to your role" : undefined}
            onClick={() => {
              if (acceptedLocked || (isChoiceLocked && !isLockedChoice("percent"))) return;
              if (!canViewContract && !isLockedChoice("percent")) return;
              setChoice("percent");
            }}
          />
          <ModeTab
            label="Suggested"
            active={choice === "suggested"}
            disabled={acceptedLocked || (isChoiceLocked ? !isLockedChoice("suggested") : !suggestedAvailable)}
            hint={!suggestedAvailable && !isLockedChoice("suggested") ? "Need ≥3 accepted comparable jobs" : undefined}
            onClick={() => {
              if (acceptedLocked || (isChoiceLocked && !isLockedChoice("suggested"))) return;
              if (!suggestedAvailable && !isLockedChoice("suggested")) return;
              setChoice("suggested");
            }}
          />
          <ModeTab
            label="Revenue Share"
            active={choice === "revenue_share"}
            disabled={acceptedLocked || (isChoiceLocked ? !isLockedChoice("revenue_share") : !canSet)}
            onClick={() => {
              if (acceptedLocked || (isChoiceLocked && !isLockedChoice("revenue_share"))) return;
              setChoice("revenue_share");
            }}
          />
        </div>
      </div>

      {/* Mode body */}
      {choice === "revenue_share" && (
        <div className="flex gap-1 p-1 rounded-md bg-muted/60">
          <ModeTab
            label="Fixed Amount"
            active={revenueShareMode === "fixed_amount"}
            disabled={acceptedLocked || (isRevenueShareModeLocked && !isLockedRevenueShareMode("fixed_amount"))}
            onClick={() => {
              if (acceptedLocked || (isRevenueShareModeLocked && !isLockedRevenueShareMode("fixed_amount"))) return;
              setRevenueShareMode("fixed_amount");
            }}
          />
          <ModeTab
            label="Percent of Net"
            active={revenueShareMode === "percentage"}
            disabled={acceptedLocked || (isRevenueShareModeLocked ? !isLockedRevenueShareMode("percentage") : !canViewContract)}
            hint={!canViewContract && !isLockedRevenueShareMode("percentage") ? "Contract value not visible to your role" : undefined}
            onClick={() => {
              if (acceptedLocked || (isRevenueShareModeLocked && !isLockedRevenueShareMode("percentage"))) return;
              if (!canViewContract && !isLockedRevenueShareMode("percentage")) return;
              setRevenueShareMode("percentage");
            }}
          />
        </div>
      )}

      {(choice === "manual" || choice === "revenue_share" && revenueShareMode === "fixed_amount") && (
        <div className="space-y-2">
          <label className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
            Offer Compensation
          </label>
          <div className="relative">
            <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              type="number"
              inputMode="decimal"
              min={0}
              step="1"
              value={amountStr}
              onChange={(e) => setAmountStr(e.target.value)}
              className="pl-7 h-8 text-sm"
              disabled={!canSet || acceptedLocked}
              data-testid="builder-manual-input"
            />
          </div>
        </div>
      )}

      {(choice === "percent" || choice === "revenue_share" && revenueShareMode === "percentage") && canViewContract && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                Net Contract
              </label>
              <Input
                value={`$${netContract.toLocaleString()}`}
                readOnly
                className="h-8 text-sm bg-muted/40"
                data-testid="builder-net-contract"
              />
              <p className="mt-1 text-[10px] text-muted-foreground">
                Contract ${contractValue.toLocaleString()} − {defaults.financeFeePct}% finance fee
                {defaults.dealKind === "cash" ? " (cash deal)" : ""}
              </p>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                Percent of Net
              </label>
              <div className="relative">
                <Input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  max={100}
                  step="0.5"
                  value={pctStr}
                  onChange={(e) => setPctStr(e.target.value)}
                  className="pr-6 h-8 text-sm"
                  disabled={!canSet || acceptedLocked}
                  data-testid="builder-percent-input"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                  %
                </span>
              </div>
              <p className="mt-1 text-[10px] text-muted-foreground">
                Default {DEFAULT_OFFER_PCT_OF_NET}% of net
              </p>
            </div>
          </div>
          <div className="rounded-md border bg-card p-2 text-xs flex items-center justify-between">
            <span className="text-muted-foreground">Calculated Offer</span>
            <span className="tabular-nums font-semibold text-sm">
              ${proposed !== null ? proposed.toLocaleString() : "—"}
            </span>
          </div>
        </div>
      )}

      {choice === "suggested" && (
        <div className="space-y-2">
          {!canViewComparables ? (
            <p className="text-xs text-muted-foreground italic">
              Comparable economics restricted.
            </p>
          ) : !suggestedAvailable ? (
            <p className="text-xs text-muted-foreground italic">
              Not enough accepted comparable jobs yet.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-md border p-2 text-center">
                  <div className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                    Low
                  </div>
                  <div className="text-sm font-semibold tabular-nums">
                    ${comparables.min!.toLocaleString()}
                  </div>
                </div>
                <div className="rounded-md border p-2 text-center bg-primary/5">
                  <div className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                    Median
                  </div>
                  <div className="text-sm font-semibold tabular-nums">
                    ${comparables.median!.toLocaleString()}
                  </div>
                </div>
                <div className="rounded-md border p-2 text-center">
                  <div className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                    High
                  </div>
                  <div className="text-sm font-semibold tabular-nums">
                    ${comparables.max!.toLocaleString()}
                  </div>
                </div>
              </div>
              <div className="flex gap-1.5">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="flex-1 h-7 text-[11px]"
                  onClick={() => applySuggested(comparables.min!)}
                  disabled={!canSet || acceptedLocked}
                >
                  Use Low
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="flex-1 h-7 text-[11px]"
                  onClick={() => applySuggested(comparables.median!)}
                  disabled={!canSet || acceptedLocked}
                >
                  Use Median
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="flex-1 h-7 text-[11px]"
                  onClick={() => applySuggested(comparables.max!)}
                  disabled={!canSet || acceptedLocked}
                >
                  Use High
                </Button>
              </div>
              <div className="rounded-md border bg-card p-2 text-xs flex items-center justify-between">
                <span className="text-muted-foreground">Selected Amount</span>
                <span className="tabular-nums font-semibold text-sm">
                  ${proposed !== null ? proposed.toLocaleString() : "—"}
                </span>
              </div>
              <p className="text-[10px] text-muted-foreground italic">
                Based on {comparables.count} accepted installer payouts. Cancelled, rescission, and finance-declined jobs excluded.
              </p>
            </>
          )}
        </div>
      )}

      <div className="space-y-2 rounded-md border bg-muted/20 p-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
              Incentives
            </div>
            <p className="mt-0.5 text-[10px] text-muted-foreground">
              Optional add-ons. These stay separate from the base payout and can be updated later, even after completion.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-[11px]"
            onClick={addIncentive}
            disabled={!canSet || acceptedLocked}
          >
            Add Incentive
          </Button>
        </div>

        {incentives.length === 0 ? (
          <p className="text-[11px] text-muted-foreground italic">
            No incentives added.
          </p>
        ) : (
          <div className="space-y-2">
            {incentives.map((item) => (
              <div key={item.id} className="rounded-md border bg-card p-2.5 space-y-2">
                <div className="flex gap-1 p-1 rounded-md bg-muted/60">
                  <ModeTab
                    label="5 Star Incentive"
                    active={item.type === "five_star"}
                    disabled={!canSet || acceptedLocked}
                    onClick={() =>
                      setIncentives((prev) =>
                        prev.map((entry) =>
                          entry.id === item.id ? { ...entry, type: "five_star", label: "" } : entry,
                        ),
                      )
                    }
                  />
                  <ModeTab
                    label="Other"
                    active={item.type === "other"}
                    disabled={!canSet || acceptedLocked}
                    onClick={() =>
                      setIncentives((prev) =>
                        prev.map((entry) =>
                          entry.id === item.id ? { ...entry, type: "other" } : entry,
                        ),
                      )
                    }
                  />
                </div>

                {item.type === "other" && (
                  <div className="space-y-1">
                    <label className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                      Incentive For
                    </label>
                    <Input
                      value={item.label}
                      onChange={(e) =>
                        setIncentives((prev) =>
                          prev.map((entry) =>
                            entry.id === item.id ? { ...entry, label: e.target.value } : entry,
                          ),
                        )
                      }
                      placeholder="Describe what this incentive is for"
                      className="h-8 text-sm"
                      disabled={!canSet || acceptedLocked}
                    />
                  </div>
                )}

                <div className="flex items-end gap-2">
                  <div className="flex-1 space-y-1">
                    <label className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                      Amount
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                      <Input
                        type="number"
                        inputMode="decimal"
                        min={0}
                        step="1"
                        value={item.amountStr}
                        onChange={(e) =>
                          setIncentives((prev) =>
                            prev.map((entry) =>
                              entry.id === item.id ? { ...entry, amountStr: e.target.value } : entry,
                            ),
                          )
                        }
                        className="pl-7 h-8 text-sm"
                        disabled={!canSet || acceptedLocked}
                      />
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 text-[11px]"
                    onClick={() => setIncentives((prev) => prev.filter((entry) => entry.id !== item.id))}
                    disabled={!canSet || acceptedLocked}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

      </div>

      <div className="flex items-center justify-between rounded-md border bg-card px-3 py-2">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
            Total Offer
          </div>
          <p className="text-[10px] text-muted-foreground">
            Base payout + incentives
          </p>
        </div>
        <div className="text-right">
          <div className="text-base font-semibold tabular-nums">
            ${effectiveOfferTotal.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Delta + reason */}
      {hasChange && (
        <p className="text-[10px] text-muted-foreground">
          {delta > 0 ? "+" : "−"}${Math.abs(delta).toLocaleString()} (
          {(pctDelta * 100).toFixed(1)}%) vs current
        </p>
      )}
      {material && (
        <Textarea
          placeholder="Reason for material change (≥10 chars)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          className="text-xs"
          disabled={acceptedLocked}
          data-testid="builder-reason"
        />
      )}

      {/* Save */}
      <div className="flex items-center justify-end">
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!canPersist || acceptedLocked}
          data-testid="builder-save"
        >
          <Check className="h-3.5 w-3.5 mr-1" />
          Save Compensation
        </Button>
      </div>

      <p className="flex items-start gap-1 text-[10px] text-muted-foreground">
        <Info className="h-3 w-3 mt-0.5 shrink-0" />
        Saves the job baseline. Offer snapshot is captured only when you send the offer.
      </p>

      <AlertDialog open={showEditConfirm} onOpenChange={setShowEditConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Edit accepted offer?</AlertDialogTitle>
            <AlertDialogDescription>
              This offer has been accepted by {acceptedOffer?.contractorName}. Continuing will allow edits to the compensation. Are you sure?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setShowEditConfirm(false); setIsEditingAccepted(false); setEditLockedChoice(null); setEditLockedRevenueShareMode(null); }}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setShowEditConfirm(false); setIsEditingAccepted(true); setEditLockedChoice(initialChoice); setEditLockedRevenueShareMode(initialRevenueShareMode); }}>Yes, edit</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showSaveConfirm} onOpenChange={setShowSaveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update accepted offer compensation?</AlertDialogTitle>
            <AlertDialogDescription>
              You are modifying an accepted offer's compensation. This will update the baseline payout.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setShowSaveConfirm(false); setIsEditingAccepted(false); setEditLockedChoice(null); setEditLockedRevenueShareMode(null); }}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setShowSaveConfirm(false); handleSave(); setIsEditingAccepted(false); setEditLockedChoice(null); setEditLockedRevenueShareMode(null); }}>Update</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
